import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Star, Menu, X, MapPin } from "lucide-react";
import { getCourseUrl } from "../constants";
import { getCourses, getSeoSettings, getLocalSeoSettings } from "../api";
import logoImg from "../assets/logo.png";

const Header = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [seo, setSeo] = useState(() => getLocalSeoSettings());
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getSeoSettings().then((data) => { if (data) setSeo(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const timer = useRef(null);
  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(v.trim().length > 0);
    clearTimeout(timer.current);
    if (!v.trim()) return setResults([]);
    timer.current = setTimeout(() => {
      getCourses({ q: v.trim(), limit: 8 }).then(setResults).catch(() => setResults([]));
    }, 200);
  };

  const go = (c) => {
    setOpen(false);
    setQuery("");
    navigate(getCourseUrl(c));
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Courses", path: "/courses" },
    { name: "States", path: "/states" },
    { name: "Top Rated", path: "/top-rated" },
    { name: "Blog", path: "/blog" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#12141a]/95 backdrop-blur-md border-b border-gray-800/80 text-white transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <img
              src={seo?.logoUrl || logoImg}
              alt={seo?.siteName || "Mini Golf Alabama"}
              className="h-11 sm:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-black tracking-tight text-white leading-tight">
                {seo?.siteName ? (
                  seo.siteName.includes(" ") ? (
                    <>
                      {seo.siteName.substring(0, seo.siteName.lastIndexOf(" "))}{" "}
                      <span className="text-[#e5243b]">
                        {seo.siteName.substring(seo.siteName.lastIndexOf(" ") + 1)}
                      </span>
                    </>
                  ) : (
                    <span className="text-[#e5243b]">{seo.siteName}</span>
                  )
                ) : (
                  <>
                    Mini Golf <span className="text-[#e5243b]">Alabama</span>
                  </>
                )}
              </span>
              <span className="text-[10px] sm:text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
                Best Courses Directory
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {navLinks.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3.5 py-2 rounded-lg text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-200 ${
                    active
                      ? "text-white bg-white/10 shadow-sm font-bold"
                      : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Search Box & Mobile Trigger */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div ref={boxRef} className="relative hidden md:block">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={handleChange}
                  onFocus={() => query.trim() && setOpen(true)}
                  placeholder="Search courses or cities..."
                  data-testid="header-search-input"
                  className="w-48 lg:w-64 pl-10 pr-4 py-2 text-xs lg:text-sm border border-gray-700/80 rounded-xl bg-[#1b1d24] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e5243b] focus:border-transparent transition-all shadow-inner"
                />
              </div>

              {open && (
                <div
                  className="absolute right-0 mt-2 w-80 bg-[#1b1d24] rounded-xl shadow-2xl border border-gray-700 overflow-hidden z-50"
                  data-testid="header-search-results"
                >
                  {results.length === 0 ? (
                    <div className="p-4 text-xs text-gray-400 text-center">
                      No courses found matching "{query}"
                    </div>
                  ) : (
                    <div className="max-h-80 overflow-y-auto py-1">
                      {results.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => go(c)}
                          className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-[#252833] transition-colors border-b border-gray-800/80 last:border-0 group"
                        >
                          <img
                            src={c.image}
                            alt={c.name}
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-white group-hover:text-[#e5243b] transition-colors truncate">
                              {c.name}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                              <MapPin className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="truncate">
                                {c.city}, {c.state?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 text-xs text-amber-400 font-bold flex-shrink-0">
                            <Star className="w-3 h-3 fill-amber-400" />
                            {c.rating}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-800 bg-[#14161d] px-4 py-4 space-y-1.5 shadow-2xl">
          {/* Mobile Search */}
          <div className="pb-3 md:hidden">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={handleChange}
                placeholder="Search courses or cities..."
                className="w-full pl-10 pr-4 py-2 text-xs border border-gray-700 rounded-xl bg-[#1b1d24] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
              />
            </div>
            {open && results.length > 0 && (
              <div className="mt-2 bg-[#1b1d24] rounded-xl border border-gray-700 overflow-hidden max-h-60 overflow-y-auto">
                {results.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setMobileOpen(false);
                      go(c);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#252833] border-b border-gray-800 last:border-0"
                  >
                    <img src={c.image} alt={c.name} className="w-8 h-8 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-white truncate">{c.name}</div>
                      <div className="text-[10px] text-gray-400">
                        {c.city}, {c.state?.toUpperCase()}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {navLinks.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`block py-2.5 px-3 rounded-xl text-sm font-semibold transition-colors ${
                  active ? "bg-[#e5243b] text-white font-bold" : "text-gray-200 hover:bg-gray-800"
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
};

export default Header;
